/**
 * Created by andrey on 31.01.2024
 */

const MultiAdsPlugin = function () {
    this.plugins = {
        msstart: new MSStartAds(),
        google: new GoogleWebAds()
    };

    AdsPlugin.call(this, connector.MULTI_ADS, {
        cantLoadAndPlayInParallel: true,
        cantLoadAfterPlay: true
    });

    Object.values(this.plugins).forEach((plugin) => {
        plugin.bus.on("changeStatus", () => this.onPluginStatus());

        AdsPlugin.TYPES.forEach((type) => {
            plugin.bus.on(`${type}:caching`, (plugin) => {
                this.bus.trigger(`${type}:caching`, plugin);
            });

            plugin.bus.on(`${type}:cached`, (code, plugin) => {
                this.onPluginCached(type, code);

                this.bus.trigger(`${type}:cached`, code, plugin);
            });

            plugin.bus.on(`${type}:impression`, (impressionCost, plugin) => {
                this.bus.trigger(`${type}:impression`, impressionCost, plugin);
            });

            plugin.bus.on(`${type}:refresh`, () => {
                this.bus.trigger(`${type}:refresh`);
            });
        });

        ["cache", "play"].forEach((stage) => {
            const eventName = `error:details:${plugin.pluginName}:${stage}`;

            plugin.bus.on(eventName, (errorCode, error) => this.bus.trigger(eventName, errorCode, error));
        });
    });
};

MultiAdsPlugin.prototype = Object.create(AdsPlugin.prototype);
MultiAdsPlugin.prototype.constructor = MultiAdsPlugin;

MultiAdsPlugin.prototype.code = MultiAdsPlugin.code = "MultiAdsPlugin";

MultiAdsPlugin.prototype._askPermission = function (callback) {
    const plugins = Object.values(this.plugins);

    callback = connector.utils.wait(plugins.length, callback);

    plugins.forEach((plugin) => {
        plugin.askPermission(callback);
    });
};

MultiAdsPlugin.prototype.listConnected = function () {
    return Object.values(this.plugins).filter((plugin) => plugin.isConnected());
};

MultiAdsPlugin.prototype.checkAdBlock = function (callback) {
    const plugins = Object.values(this.plugins).filter((plugin) => plugin.checkAdBlock);
    let blocked = false;
    const done = connector.utils.wait(plugins.length, () => callback(blocked));

    plugins.forEach((plugin) => {
        plugin.checkAdBlock((result) => {
            blocked = blocked || result === true;

            done();
        });
    });
};

// the status is derived from the sub-plugins, connected while any of them is: a sub-plugin that connects late
// (Google's onReady after the init timeout) or drops out is reflected at once instead of being sampled on connect
MultiAdsPlugin.prototype.onPluginStatus = function () {
    if (this.connectWaiter) {
        this.connectWaiter();
    }

    if (!this.connecting) {
        this.setStatus(this.listConnected().length ? connector.STATUS_CONNECTED : connector.STATUS_DISCONNECTED);
    }
};

MultiAdsPlugin.prototype.connect = function () {
    Object.values(this.plugins).forEach((plugin) => {
        plugin.connect();
    });

    AdsPlugin.prototype.connect.call(this);
};

// settles as soon as one sub-plugin is connected, or once none is connecting any more; whatever connects after that
// is picked up by onPluginStatus, so an early DISCONNECTED is not final
MultiAdsPlugin.prototype._connect = function (callback) {
    const settle = connector.utils.once((status) => {
        this.connectWaiter = undefined;
        callback(status);
    });

    this.connectWaiter = () => {
        if (this.listConnected().length) {
            settle(connector.STATUS_CONNECTED);
        } else if (!Object.values(this.plugins).some((plugin) => plugin.connecting)) {
            settle(connector.STATUS_DISCONNECTED);
        }
    };

    this.connectWaiter();
};

MultiAdsPlugin.prototype.cacheAd = function (name, callback) {
    if (this.units[name].isCached() && !this.isAdExpired(name)) {
        Object.values(this.plugins).forEach((plugin) => {
            plugin.cacheAd(name);
        });
    }

    AdsPlugin.prototype.cacheAd.call(this, name, callback);
};

MultiAdsPlugin.prototype.cacheAdInner = function (name, callback) {
    const unit = this.units[name];
    unit.setLoaderState(AdsPlugin.LOADER_STATE.LOADING);

    const plugins = Object.values(this.plugins);
    let counter = plugins.length;

    const onCompleted = connector.utils.once((code) => {
        if (code === connector.CODE_SUCCEED) {
            unit.cached = true;
            unit.setLoaderState(AdsPlugin.LOADER_STATE.READY);
        } else {
            unit.cachedErrorTime = Date.now();
            unit.setLoaderState(AdsPlugin.LOADER_STATE.IDLE);
        }

        callback(code);
    });

    Object.values(this.plugins).forEach((plugin) => {
        plugin.cacheAd(name, (code) => {
            counter--;

            if (code === connector.CODE_SUCCEED) {
                onCompleted(code);
                return;
            }

            if (counter === 0) {
                onCompleted(connector.CODE_FAILED);
            }
        });
    });
};

// a sub-plugin may cache an ad after it has already answered "timeout" to cacheAdInner (late load) -
// pick it up at once instead of waiting out the own load error block
MultiAdsPlugin.prototype.onPluginCached = function (name, code) {
    const unit = this.units[name];

    if (code !== connector.CODE_SUCCEED || unit.isLoading() || unit.isCached() || unit.isRunning()) {
        return;
    }

    unit.cached = true;
    unit.cachedErrorTime = undefined;
    unit.setLoaderState(AdsPlugin.LOADER_STATE.READY);
};

MultiAdsPlugin.prototype.hasCachedAd = function (plugin, name) {
    return plugin.isConnected() && plugin.units[name].isCached() && !plugin.isAdExpired(name);
};

// a failure before anything was displayed - no fill, expired, throttled, not connected - moves on to the next
// plugin; a blocker and a shown ad are final
MultiAdsPlugin.isFallbackCode = function (code) {
    return [
        ERRORS.NOT_CONNECTED,
        ERRORS.SHOW_REWARDED.NOT_SUPPORTED,
        ERRORS.SHOW_REWARDED.NO_ADS,
        ERRORS.SHOW_REWARDED.NOT_CONFIGURED,
        ERRORS.SHOW_REWARDED.THROTTLED,
        ERRORS.SHOW_REWARDED.AD_EXPIRED,
        ERRORS.SHOW_INTERSTITIAL.NOT_SUPPORTED,
        ERRORS.SHOW_INTERSTITIAL.NO_ADS,
        ERRORS.SHOW_INTERSTITIAL.NOT_CONFIGURED,
        ERRORS.SHOW_INTERSTITIAL.THROTTLED
    ].includes(code);
};

MultiAdsPlugin.prototype.playAd = function (name, callback) {
    callback = callback || function () {};

    const noAdsCode = name === AdsPlugin.REWARDED ? ERRORS.SHOW_REWARDED.NO_ADS : ERRORS.SHOW_INTERSTITIAL.NO_ADS;
    const plugins = Object.values(this.plugins);
    const ready = plugins.filter((plugin) => this.hasCachedAd(plugin, name));

    // a cached ad plays first; the rest are fallbacks that may load only after a failed show request
    const queue = ready.concat(plugins.filter((plugin) => !ready.includes(plugin) && plugin.isConnected()));

    const unit = this.units[name];
    unit.consumeCachedAd();
    unit.setLoaderState(AdsPlugin.LOADER_STATE.IDLE);
    unit.setVideoState(AdsPlugin.VIDEO_STATE.PLAYING);
    this.bus.trigger(`${name}:start`, this.pluginName);

    const finish = (plugin, code, data = {}) => {
        unit.finishTime = Date.now();
        unit.setVideoState(AdsPlugin.VIDEO_STATE.IDLE);

        const success = code === connector.CODE_SUCCEED;
        this.bus.trigger(`${name}:close`, success, plugin.pluginName);

        if (name === AdsPlugin.REWARDED && success) {
            this.bus.trigger("rewarded:reward", plugin.pluginName);
        }

        callback(code, { pluginName: plugin.pluginName, raw: data.raw });
    };

    const playNext = (lastPlugin, lastCode, lastData, canLoad) => {
        const plugin = queue.shift();

        if (!plugin) {
            finish(lastPlugin || plugins[0], lastCode || (lastPlugin ? noAdsCode : ERRORS.NOT_CONNECTED), lastData);
            return;
        }

        if (!canLoad && !this.hasCachedAd(plugin, name)) {
            playNext(lastPlugin, lastCode, lastData, canLoad);
            return;
        }

        console.log(`Play ${plugin.pluginName}`);

        const player = new ConnectorActionPlayer([
            function (success, failure) {
                plugin.cacheAd(name, (code) => {
                    if (code === connector.CODE_SUCCEED) {
                        success();
                    } else {
                        failure(noAdsCode, lastData);
                    }
                });
            },
            function (success, failure) {
                plugin.playAd(name, connector.utils.once((code, data) => {
                    if (code === connector.CODE_SUCCEED) {
                        success();
                    } else {
                        failure(code, data);
                    }
                }));
            }
        ]);

        player.onSuccess(() => finish(plugin, connector.CODE_SUCCEED));
        player.onFailure((code, data) => {
            if (MultiAdsPlugin.isFallbackCode(code)) {
                playNext(plugin, code, data, true);
            } else {
                finish(plugin, code, data);
            }
        });
        player.play();
    };

    playNext(undefined, undefined, undefined, ready.length === 0);
};

MultiAdsPlugin.prototype.isStickySupported = function () {
    return this.plugins.google.isStickySupported();
};

MultiAdsPlugin.prototype.isStickyAvailable = function () {
    return this.plugins.google.isStickyAvailable();
};

MultiAdsPlugin.prototype.showSticky = function (callback) {
    this.plugins.google.showSticky(callback);
};

MultiAdsPlugin.prototype.closeSticky = function (callback) {
    this.plugins.google.closeSticky(callback);
};

MultiAdsPlugin.prototype.onImpressionEvent = function (name, impressionCost) {
    if (name === AdsPlugin.STICKY) {
        this.plugins.google.onImpressionEvent(name, impressionCost);
    } else {
        AdsPlugin.prototype.onImpressionEvent.call(this, name, impressionCost);
    }
};

MultiAdsPlugin.isAppropriate = function () {
    return MSStartAds.isAppropriate() && GoogleWebAds.isAppropriate(); 
};

MultiAdsPlugin.prototype.isNoAdsProductAvailable = function () {
    return true;
};
